import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LocateFixed, Minus, Move, Plus } from 'lucide-react';
import type { TablePosition, FloorPlan } from '../types';

interface FloorPlanCanvasProps {
  floorPlan: FloorPlan;
  onTableUpdate: (table: TablePosition) => void;
  onTableSelect: (table: TablePosition | null) => void;
  selectedTable?: TablePosition | null;
  highlightedTableNumber?: number;
  isEditable?: boolean;
  className?: string;
}

const DEFAULT_SCALE = 0.78;
const MIN_SCALE = 0.35;
const MAX_SCALE = 2.4;
const GRID_SIZE = 24;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + safeRadius, y);
  ctx.lineTo(x + width - safeRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  ctx.lineTo(x + width, y + height - safeRadius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  ctx.lineTo(x + safeRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  ctx.lineTo(x, y + safeRadius);
  ctx.quadraticCurveTo(x, y, x + safeRadius, y);
  ctx.closePath();
};

const getPresetColor = (shape: TablePosition['shape']) => {
  switch (shape) {
    case 'round':
      return '#efe4d3';
    case 'square':
      return '#f3e9db';
    case 'rectangle':
      return '#ead8c4';
  }
};

const getLocalPoint = (table: TablePosition, x: number, y: number) => {
  const centerX = table.x + table.width / 2;
  const centerY = table.y + table.height / 2;
  const dx = x - centerX;
  const dy = y - centerY;
  const radians = (table.rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  return {
    x: dx * cos + dy * sin,
    y: -dx * sin + dy * cos,
  };
};

const isPointInsideTable = (table: TablePosition, x: number, y: number) => {
  const local = getLocalPoint(table, x, y);
  const halfWidth = table.width / 2;
  const halfHeight = table.height / 2;

  if (table.shape === 'round') {
    const radius = Math.min(halfWidth, halfHeight);
    return local.x * local.x + local.y * local.y <= radius * radius;
  }

  return Math.abs(local.x) <= halfWidth && Math.abs(local.y) <= halfHeight;
};

const distributeSeats = (count: number, sides: number) => {
  const safeCount = Math.max(count, sides);
  const counts = new Array(sides).fill(Math.floor(safeCount / sides));

  for (let index = 0; index < safeCount % sides; index += 1) {
    counts[index] += 1;
  }

  return counts;
};

const createEvenlySpacedValues = (count: number, start: number, end: number) => {
  if (count <= 0) {
    return [];
  }

  if (count === 1) {
    return [(start + end) / 2];
  }

  return Array.from({ length: count }, (_, index) => start + ((end - start) * index) / (count - 1));
};

const getSeatPositions = (table: TablePosition) => {
  const halfWidth = table.width / 2;
  const halfHeight = table.height / 2;
  const seatDistance = clamp(Math.min(table.width, table.height) * 0.18, 12, 20);

  if (table.shape === 'round') {
    const radius = Math.min(halfWidth, halfHeight);
    const seatRadius = radius + seatDistance;
    const seatCount = Math.max(4, table.capacity);

    return Array.from({ length: seatCount }, (_, index) => {
      const angle = -Math.PI / 2 + (index * Math.PI * 2) / seatCount;
      return {
        x: Math.cos(angle) * seatRadius,
        y: Math.sin(angle) * seatRadius,
        rotation: angle + Math.PI / 2,
      };
    });
  }

  if (table.shape === 'rectangle') {
    const top = Math.max(2, Math.ceil(table.capacity * 0.35));
    const bottom = Math.max(2, Math.floor(table.capacity * 0.35));
    const remaining = Math.max(2, table.capacity - top - bottom);
    const left = Math.ceil(remaining / 2);
    const right = remaining - left;
    const inset = clamp(Math.min(table.width, table.height) * 0.2, 10, 18);
    const topXs = createEvenlySpacedValues(top, -halfWidth + inset, halfWidth - inset);
    const bottomXs = createEvenlySpacedValues(bottom, -halfWidth + inset, halfWidth - inset);
    const leftYs = createEvenlySpacedValues(left, -halfHeight + inset, halfHeight - inset);
    const rightYs = createEvenlySpacedValues(right, -halfHeight + inset, halfHeight - inset);

    return [
      ...topXs.map((x) => ({ x, y: -halfHeight - seatDistance, rotation: 0 })),
      ...bottomXs.map((x) => ({ x, y: halfHeight + seatDistance, rotation: Math.PI })),
      ...leftYs.map((y) => ({ x: -halfWidth - seatDistance, y, rotation: -Math.PI / 2 })),
      ...rightYs.map((y) => ({ x: halfWidth + seatDistance, y, rotation: Math.PI / 2 })),
    ];
  }

  const [top, right, bottom, left] = distributeSeats(table.capacity, 4);
  const inset = clamp(Math.min(table.width, table.height) * 0.18, 10, 16);
  const topXs = createEvenlySpacedValues(top, -halfWidth + inset, halfWidth - inset);
  const bottomXs = createEvenlySpacedValues(bottom, -halfWidth + inset, halfWidth - inset);
  const leftYs = createEvenlySpacedValues(left, -halfHeight + inset, halfHeight - inset);
  const rightYs = createEvenlySpacedValues(right, -halfHeight + inset, halfHeight - inset);

  return [
    ...topXs.map((x) => ({ x, y: -halfHeight - seatDistance, rotation: 0 })),
    ...bottomXs.map((x) => ({ x, y: halfHeight + seatDistance, rotation: Math.PI })),
    ...leftYs.map((y) => ({ x: -halfWidth - seatDistance, y, rotation: -Math.PI / 2 })),
    ...rightYs.map((y) => ({ x: halfWidth + seatDistance, y, rotation: Math.PI / 2 })),
  ];
};

export default function FloorPlanCanvas({
  floorPlan,
  onTableUpdate,
  onTableSelect,
  selectedTable,
  highlightedTableNumber,
  isEditable = false,
  className = '',
}: FloorPlanCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [touches, setTouches] = useState<{ [key: number]: { x: number; y: number } }>({});
  const [lastPinchDistance, setLastPinchDistance] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const screenToCanvas = useCallback((clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) {
      return null;
    }

    return {
      x: (clientX - rect.left - pan.x * scale) / scale,
      y: (clientY - rect.top - pan.y * scale) / scale,
    };
  }, [pan.x, pan.y, scale]);

  const drawChair = useCallback((
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    rotation: number,
    isHighlighted: boolean,
  ) => {
    const chairWidth = 12;
    const chairHeight = 9;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);

    ctx.fillStyle = isHighlighted ? '#fff4cf' : 'rgba(255, 255, 255, 0.96)';
    ctx.strokeStyle = isHighlighted ? '#d5a100' : 'rgba(91, 72, 51, 0.22)';
    ctx.lineWidth = 1.2;
    drawRoundedRect(ctx, -chairWidth / 2, -chairHeight / 2, chairWidth, chairHeight, 3);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = isHighlighted ? '#ffe38e' : 'rgba(91, 72, 51, 0.12)';
    drawRoundedRect(ctx, -chairWidth / 2, -chairHeight / 2 - 3, chairWidth, 3.5, 2);
    ctx.fill();

    ctx.restore();
  }, []);

  const drawTable = useCallback((
    ctx: CanvasRenderingContext2D,
    table: TablePosition,
    isSelected = false,
    isHighlighted = false,
  ) => {
    const baseColor = table.color || getPresetColor(table.shape);
    const halfWidth = table.width / 2;
    const halfHeight = table.height / 2;
    const seats = getSeatPositions(table);

    ctx.save();
    ctx.translate(table.x + halfWidth, table.y + halfHeight);
    ctx.rotate((table.rotation * Math.PI) / 180);

    seats.forEach((seat) => drawChair(ctx, seat.x, seat.y, seat.rotation, isHighlighted));

    ctx.shadowColor = isHighlighted
      ? 'rgba(213, 161, 0, 0.45)'
      : isSelected
        ? 'rgba(37, 90, 84, 0.28)'
        : 'rgba(79, 58, 35, 0.16)';
    ctx.shadowBlur = isHighlighted ? 26 : 18;
    ctx.shadowOffsetY = 6;

    const gradient = ctx.createLinearGradient(-halfWidth, -halfHeight, halfWidth, halfHeight);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.92)');
    gradient.addColorStop(1, baseColor);

    ctx.fillStyle = gradient;
    ctx.strokeStyle = isHighlighted ? '#d5a100' : isSelected ? '#255a54' : 'rgba(91, 72, 51, 0.28)';
    ctx.lineWidth = isHighlighted ? 3.5 : isSelected ? 3 : 2;

    if (table.shape === 'round') {
      ctx.beginPath();
      ctx.arc(0, 0, Math.min(halfWidth, halfHeight), 0, Math.PI * 2);
    } else {
      drawRoundedRect(ctx, -halfWidth, -halfHeight, table.width, table.height, table.shape === 'rectangle' ? 16 : 14);
    }

    ctx.fill();
    ctx.stroke();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.lineWidth = 1;
    if (table.shape === 'round') {
      ctx.beginPath();
      ctx.arc(0, 0, Math.max(8, Math.min(halfWidth, halfHeight) - 8), 0, Math.PI * 2);
      ctx.stroke();
    } else {
      drawRoundedRect(ctx, -halfWidth + 6, -halfHeight + 6, table.width - 12, table.height - 12, table.shape === 'rectangle' ? 12 : 10);
      ctx.stroke();
    }

    const badgeRadius = Math.min(Math.max(Math.min(table.width, table.height) * 0.22, 12), 18);
    ctx.fillStyle = isHighlighted ? '#f6bf26' : isSelected ? '#255a54' : 'rgba(66, 51, 35, 0.78)';
    ctx.beginPath();
    ctx.arc(0, 0, badgeRadius, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = isHighlighted ? '#4b3506' : '#ffffff';
    ctx.font = `700 ${Math.max(12, Math.min(table.width, table.height) * 0.23)}px "Plus Jakarta Sans", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(table.tableNumber.toString(), 0, 0.5);

    if (table.width > 70 || table.height > 70) {
      ctx.fillStyle = 'rgba(66, 51, 35, 0.65)';
      ctx.font = `600 ${Math.max(10, Math.min(table.width, table.height) * 0.12)}px "Plus Jakarta Sans", sans-serif`;
      ctx.fillText(`${table.capacity} seats`, 0, badgeRadius + 13);
    }

    ctx.restore();
  }, [drawChair]);

  const drawVenueBackdrop = useCallback((ctx: CanvasRenderingContext2D) => {
    const baseBackground = floorPlan.background || '#f7efe6';
    ctx.fillStyle = baseBackground;
    ctx.fillRect(0, 0, floorPlan.width, floorPlan.height);

    const atmosphere = ctx.createLinearGradient(0, 0, floorPlan.width, floorPlan.height);
    atmosphere.addColorStop(0, 'rgba(255,255,255,0.38)');
    atmosphere.addColorStop(1, 'rgba(86, 62, 40, 0.04)');
    ctx.fillStyle = atmosphere;
    ctx.fillRect(0, 0, floorPlan.width, floorPlan.height);

    ctx.strokeStyle = isEditable ? 'rgba(91, 72, 51, 0.08)' : 'rgba(91, 72, 51, 0.05)';
    ctx.lineWidth = 1;

    for (let x = 0; x <= floorPlan.width; x += GRID_SIZE) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, floorPlan.height);
      ctx.stroke();
    }

    for (let y = 0; y <= floorPlan.height; y += GRID_SIZE) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(floorPlan.width, y);
      ctx.stroke();
    }

    drawRoundedRect(ctx, 12, 12, floorPlan.width - 24, floorPlan.height - 24, 26);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(86, 62, 40, 0.22)';
    ctx.lineWidth = 2;
    ctx.stroke();

    const title = floorPlan.name || 'Venue Layout';
    ctx.font = '700 16px "Plus Jakarta Sans", sans-serif';
    const textWidth = ctx.measureText(title).width;
    drawRoundedRect(ctx, 24, 24, textWidth + 30, 34, 17);
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(86, 62, 40, 0.12)';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = 'rgba(43, 36, 29, 0.9)';
    ctx.textBaseline = 'middle';
    ctx.fillText(title, 39, 41);

    if (isEditable) {
      drawRoundedRect(ctx, floorPlan.width - 182, 24, 146, 30, 15);
      ctx.fillStyle = 'rgba(37, 90, 84, 0.1)';
      ctx.fill();
      ctx.fillStyle = '#255a54';
      ctx.font = '600 12px "Plus Jakarta Sans", sans-serif';
      ctx.fillText('Drag to position tables', floorPlan.width - 168, 39);
    }
  }, [floorPlan.background, floorPlan.height, floorPlan.name, floorPlan.width, isEditable]);

  const drawFloorPlan = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(scale, scale);
    ctx.translate(pan.x, pan.y);

    drawVenueBackdrop(ctx);

    floorPlan.tables.forEach((table) => {
      const isSelected = selectedTable?.id === table.id;
      const isHighlighted = highlightedTableNumber === table.tableNumber;
      drawTable(ctx, table, isSelected, isHighlighted);
    });

    ctx.restore();
  }, [drawTable, drawVenueBackdrop, floorPlan.tables, highlightedTableNumber, pan.x, pan.y, scale, selectedTable?.id]);

  const getTableAtPosition = useCallback((x: number, y: number) => {
    for (let index = floorPlan.tables.length - 1; index >= 0; index -= 1) {
      const table = floorPlan.tables[index];
      if (isPointInsideTable(table, x, y)) {
        return table;
      }
    }

    return null;
  }, [floorPlan.tables]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const point = screenToCanvas(e.clientX, e.clientY);
    if (!point) {
      return;
    }

    const table = getTableAtPosition(point.x, point.y);

    if (table && isEditable) {
      onTableSelect(table);
      setIsDragging(true);
      setDragOffset({
        x: point.x - table.x,
        y: point.y - table.y,
      });
    } else {
      if (isEditable) {
        onTableSelect(null);
      }
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  }, [getTableAtPosition, isEditable, onTableSelect, screenToCanvas]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && selectedTable && isEditable) {
      const point = screenToCanvas(e.clientX, e.clientY);
      if (!point) {
        return;
      }

      const newX = clamp(point.x - dragOffset.x, 0, floorPlan.width - selectedTable.width);
      const newY = clamp(point.y - dragOffset.y, 0, floorPlan.height - selectedTable.height);
      const snappedX = Math.round(newX / GRID_SIZE) * GRID_SIZE;
      const snappedY = Math.round(newY / GRID_SIZE) * GRID_SIZE;

      onTableUpdate({
        ...selectedTable,
        x: snappedX,
        y: snappedY,
      });
      return;
    }

    if (isPanning) {
      const deltaX = (e.clientX - panStart.x) / scale;
      const deltaY = (e.clientY - panStart.y) / scale;

      setPan((previousPan) => ({
        x: previousPan.x + deltaX,
        y: previousPan.y + deltaY,
      }));
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  }, [dragOffset.x, dragOffset.y, floorPlan.height, floorPlan.width, isDragging, isEditable, isPanning, onTableUpdate, panStart.x, panStart.y, scale, screenToCanvas, selectedTable]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsPanning(false);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();

    const nextTouches: { [key: number]: { x: number; y: number } } = {};
    for (let index = 0; index < e.touches.length; index += 1) {
      const touch = e.touches[index];
      nextTouches[touch.identifier] = { x: touch.clientX, y: touch.clientY };
    }
    setTouches(nextTouches);

    if (e.touches.length === 2) {
      const [touch1, touch2] = [e.touches[0], e.touches[1]];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      setLastPinchDistance(distance);
    } else {
      setLastPinchDistance(null);
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();

    if (e.touches.length === 2 && lastPinchDistance !== null) {
      const [touch1, touch2] = [e.touches[0], e.touches[1]];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      const pinchRatio = distance / lastPinchDistance;
      const scaleDelta = (pinchRatio - 1) * 0.5;

      setScale((previousScale) => clamp(previousScale + scaleDelta, MIN_SCALE, MAX_SCALE));
      setLastPinchDistance(distance);
    } else if (e.touches.length === 1 && !lastPinchDistance) {
      const touch = e.touches[0];
      const previousTouch = touches[touch.identifier];

      if (previousTouch) {
        const deltaX = (touch.clientX - previousTouch.x) / scale;
        const deltaY = (touch.clientY - previousTouch.y) / scale;

        setPan((previousPan) => ({
          x: previousPan.x + deltaX,
          y: previousPan.y + deltaY,
        }));
      }

      setTouches((previousTouches) => ({
        ...previousTouches,
        [touch.identifier]: { x: touch.clientX, y: touch.clientY },
      }));
    }
  }, [lastPinchDistance, scale, touches]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const remainingTouches: { [key: number]: { x: number; y: number } } = {};
    for (let index = 0; index < e.touches.length; index += 1) {
      const touch = e.touches[index];
      remainingTouches[touch.identifier] = { x: touch.clientX, y: touch.clientY };
    }
    setTouches(remainingTouches);

    if (e.touches.length < 2) {
      setLastPinchDistance(null);
    }
  }, []);

  useEffect(() => {
    const checkIsMobile = () => {
      const userAgent = navigator.userAgent;
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isCompactScreen = window.innerWidth <= 768;
      setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) || (hasTouch && isCompactScreen));
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    if (!highlightedTableNumber) {
      return;
    }

    const table = floorPlan.tables.find((entry) => entry.tableNumber === highlightedTableNumber);
    if (!table) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const nextScale = DEFAULT_SCALE;
    const centerX = -(table.x + table.width / 2 - canvas.width / 2 / nextScale);
    const centerY = -(table.y + table.height / 2 - canvas.height / 2 / nextScale);

    setPan({ x: centerX, y: centerY });
    setScale(nextScale);
  }, [floorPlan.tables, highlightedTableNumber]);

  useEffect(() => {
    drawFloorPlan();
  }, [drawFloorPlan]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const updateCanvasSize = () => {
      const container = canvas.parentElement;
      if (!container) {
        return;
      }

      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
      drawFloorPlan();
    };

    const handleWheelEvent = (e: WheelEvent) => {
      e.preventDefault();
      const delta = -e.deltaY / 1000;
      setScale((previousScale) => clamp(previousScale + delta, MIN_SCALE, MAX_SCALE));
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    canvas.addEventListener('wheel', handleWheelEvent, { passive: false });

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      canvas.removeEventListener('wheel', handleWheelEvent);
    };
  }, [drawFloorPlan]);

  return (
    <div className={`floor-plan-container ${className}`}>
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          display: 'block',
          cursor: isDragging || isPanning ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      />

      <div className="floor-plan-controls">
        <button type="button" className="floor-plan-control-button" onClick={() => setScale((value) => clamp(value * 1.15, MIN_SCALE, MAX_SCALE))}>
          <Plus size={16} />
        </button>
        <button type="button" className="floor-plan-control-button" onClick={() => setScale((value) => clamp(value / 1.15, MIN_SCALE, MAX_SCALE))}>
          <Minus size={16} />
        </button>
        <button
          type="button"
          className="floor-plan-control-button"
          onClick={() => {
            setScale(DEFAULT_SCALE);
            setPan({ x: 0, y: 0 });
          }}
        >
          <LocateFixed size={16} />
        </button>
      </div>

      <div className="floor-plan-scale">
        <span>{Math.round(scale * 100)}%</span>
        {!isMobile && <span className="floor-plan-scale__hint"><Move size={12} /> Drag to pan</span>}
      </div>

      {highlightedTableNumber && (
        <div className="floor-plan-badge floor-plan-badge--highlight">
          Table {highlightedTableNumber} highlighted
        </div>
      )}

      {isEditable && selectedTable && (
        <div className="floor-plan-badge">
          Editing Table {selectedTable.tableNumber}
        </div>
      )}

      {isMobile && (
        <div className="floor-plan-mobile-hint" style={{ opacity: Object.keys(touches).length === 0 ? 1 : 0 }}>
          Pinch to zoom. Drag to pan.
        </div>
      )}
    </div>
  );
}
